import type {Evaluator} from "@cm/graph-v3"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {z} from "zod/v4-mini"
import {hashObject} from "@cm/utils/hashing"
import {ImageNode} from "#template/nodes/image/image-node"
import type {OnFilterActiveContext, TemplateNodeContext, TemplateNodeDefinition, VisitorNodeResult} from "#template/template-node/types"
import {skipped} from "#template/template-node/constants"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import {imageLike} from "#template/nodes/image/fundamental"
import type {ImageLikeNode} from "#template/nodes/types"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {ImageGenerator, Vec2} from "@cm/material-nodes"
import {vec2, RgbCurve, materialConnection} from "@cm/material-nodes"
import {visitNone} from "#template/template-node/visit-helpers"

const curveControlPoints = z.array(vec2)
export type CurveControlPoints = Vec2[]

export type ImageRgbCurveParameters = {
    name: string
    input: ImageLikeNode | null
    r: CurveControlPoints
    g: CurveControlPoints
    b: CurveControlPoints
    rgb: CurveControlPoints
}

const imageRgbCurveDefinition: TemplateNodeDefinition<ImageRgbCurveParameters> = {
    nodeClass: "ImageRgbCurve",
    validation: {
        parameters: z.object({
            name: z.string(),
            input: z.nullable(imageLike),
            r: curveControlPoints,
            g: curveControlPoints,
            b: curveControlPoints,
            rgb: curveControlPoints,
        }),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class ImageRgbCurve extends ImageNode<ImageRgbCurveParameters> {
    public override get definition() {
        return imageRgbCurveDefinition
    }

    public override onFilterActive(_context: OnFilterActiveContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<ImageRgbCurveParameters> {
        if (this.parameters.input === null) return skipped
        return visitNone(this.parameters)
    }

    public override evaluate(scope: GraphBuilderScope, evaluator: NodeEvaluator): BuilderInlet<ImageGenerator | null> {
        const {input, ...rest} = this.parameters
        const inputImage = evaluator.evaluate(input)

        return scope.pureLambda<[ImageGenerator | null, CurveControlPoints, CurveControlPoints, CurveControlPoints, CurveControlPoints], ImageGenerator | null>(
            scope.tuple(inputImage, rest.r, rest.g, rest.b, rest.rgb),
            ([inputImage, r, g, b, rgb]) => {
                if (!inputImage) return null

                const {imageNode, metadata} = inputImage

                return {
                    imageNode: {
                        generator: (generatorData) => {
                            const image = imageNode.generator(generatorData)

                            const toCurveParameters = (curve: Vec2[], _index: number) => {
                                const sortedCurve = [...curve].sort((a, b) => a.x - b.x)
                                return sortedCurve
                            }

                            return {
                                color: materialConnection(
                                    new RgbCurve({
                                        parameters: {
                                            inputs: {
                                                color: image.color,
                                            },
                                            settings: {
                                                fac: 1,
                                                ...RgbCurve.settingsFromControlPoints([
                                                    toCurveParameters(r, 0),
                                                    toCurveParameters(g, 1),
                                                    toCurveParameters(b, 2),
                                                    toCurveParameters(rgb, 3),
                                                ]),
                                            },
                                        },
                                    }),
                                    "color",
                                ),
                                alpha: image.alpha,
                            }
                        },
                        hash: hashObject({
                            type: "ImageRgbCurve",
                            inputImage: imageNode.hash,
                            r,
                            g,
                            b,
                            rgb,
                        }),
                    },
                    metadata,
                }
            },
            "imageRgbCurve",
        )
    }
}
